import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ADMIN_TOKEN_KEY, SUPPORT_TOKEN_KEY, getActiveToken } from "./sessionToken";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contractStatus, setContractStatus] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractError, setContractError] = useState(false);
  const contractRequest = useRef(null);

  const refreshContractStatus = useCallback(() => {
    if (contractRequest.current) return contractRequest.current;
    const request = (async () => {
      setContractLoading(true);
      setContractError(false);
      try {
        const token = getActiveToken();
        if (!token) return;
        const { data } = await axios.get(`${API}/contract/current`, {
          headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
        });
        if (getActiveToken() === token) setContractStatus(data);
      } catch {
        setContractError(true);
      } finally { setContractLoading(false); }
    })();
    contractRequest.current = request;
    request.finally(() => { if (contractRequest.current === request) contractRequest.current = null; });
    return request;
  }, []);

  const restoreAdmin = useCallback(async () => {
    sessionStorage.removeItem(SUPPORT_TOKEN_KEY);
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (token) {
      try {
        const { data } = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
        });
        if (data.role !== "superadmin") throw new Error("Sessione amministrativa non valida");
        if (localStorage.getItem(ADMIN_TOKEN_KEY) !== token) return false;
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        setUser(data);
        setContractStatus(null);
        setContractError(false);
        return true;
      } catch { /* The original admin token may also have expired. */ }
    }
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    delete axios.defaults.headers.common.Authorization;
    setUser(null);
    setContractStatus(null);
    return false;
  }, []);

  const refreshUser = useCallback(async () => {
    const token = getActiveToken();
    if (!token) return null;
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
      });
      if (getActiveToken() !== token) return null;
      if (data.token) {
        if (sessionStorage.getItem(SUPPORT_TOKEN_KEY) === token) sessionStorage.setItem(SUPPORT_TOKEN_KEY, data.token);
        else localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
      }
      setUser(data);
      setContractStatus((previous) => {
        const snapshot = data.contract;
        if (!snapshot) return previous;
        // Keep the signed login decision, but preserve the full preview (and
        // its updated date) while the signer is completing that same session.
        if (previous?.needs_acceptance && snapshot.needs_acceptance && previous.is_signer) {
          return { ...snapshot, ...previous };
        }
        return snapshot;
      });
      setContractError(false);
      return data;
    } catch (error) {
      if (getActiveToken() === token && [401, 403].includes(error.response?.status)) {
        if (sessionStorage.getItem(SUPPORT_TOKEN_KEY) === token) {
          const ok = await restoreAdmin();
          window.location.assign(ok ? "/admin" : "/login");
        } else {
          localStorage.removeItem(ADMIN_TOKEN_KEY);
          delete axios.defaults.headers.common.Authorization;
          setUser(null);
          setContractStatus(null);
        }
      }
      return null;
    }
  }, [restoreAdmin]);

  useEffect(() => {
    const token = getActiveToken();
    if (!token) { setLoading(false); return; }
    // Ensure the raw axios instance also has the header, so any legacy calls work.
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  useEffect(() => {
    window.addEventListener("focus", refreshUser);
    window.addEventListener("subscription-changed", refreshUser);
    window.addEventListener("password-required", refreshUser);
    window.addEventListener("impersonation-expired", refreshUser);
    return () => {
      window.removeEventListener("focus", refreshUser);
      window.removeEventListener("subscription-changed", refreshUser);
      window.removeEventListener("password-required", refreshUser);
      window.removeEventListener("impersonation-expired", refreshUser);
    };
  }, [refreshUser]);

  const userId = user?.id;
  useEffect(() => {
    if (!userId) return undefined;
    // Keep the persistent banner in sync even if an admin suspends an open tab.
    const timer = window.setInterval(refreshUser, 60000);
    return () => window.clearInterval(timer);
  }, [userId, refreshUser]);

  const login = async (email, password) => {
    sessionStorage.removeItem(SUPPORT_TOKEN_KEY);
    delete axios.defaults.headers.common.Authorization;
    const r = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    localStorage.setItem(ADMIN_TOKEN_KEY, r.data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${r.data.token}`;
    setUser(r.data.user);
    setContractStatus(r.data.contract);
    setContractError(false);
    return r.data.user;
  };
  const completeContractAcceptance = (data) => {
    localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    setContractStatus(data.contract);
    setContractError(false);
  };
  const startImpersonation = async (tenantId) => {
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (user?.role !== "superadmin" || !adminToken || sessionStorage.getItem(SUPPORT_TOKEN_KEY)) {
      throw new Error("Accedi come SuperAdmin prima di iniziare il supporto.");
    }
    const { data } = await axios.post(`${API}/auth/impersonation/start`, { tenant_id: tenantId }, {
      headers: { Authorization: `Bearer ${adminToken}` },
    });
    sessionStorage.setItem(SUPPORT_TOKEN_KEY, data.token);
    axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
    const current = await refreshUser();
    if (!current?.impersonation) throw new Error("Impossibile avviare la sessione di supporto.");
    return current;
  };

  const stopImpersonation = async () => {
    const token = sessionStorage.getItem(SUPPORT_TOKEN_KEY);
    if (!token || !user?.impersonation?.session_id) return false;
    try {
      await axios.post(`${API}/auth/impersonation/stop`, {
        session_id: user.impersonation.session_id,
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {
      if (error.response?.status !== 401) throw error;
    }
    const ok = await restoreAdmin();
    if (!ok) window.location.assign("/login");
    return ok;
  };

  const logout = () => {
    const token = getActiveToken();
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (sessionStorage.getItem(SUPPORT_TOKEN_KEY) && user?.impersonation?.session_id) {
      axios.post(`${API}/auth/impersonation/stop`, { session_id: user.impersonation.session_id }, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    sessionStorage.removeItem(SUPPORT_TOKEN_KEY);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
    setContractStatus(null);
    setContractError(false);
    if (adminToken || token) axios.post(`${API}/auth/logout`, {}, {
      headers: { Authorization: `Bearer ${adminToken || token}` }, withCredentials: true,
    }).catch(() => {});
  };

  const isSuperadmin = user?.role === "superadmin";
  const subscriptionDisabled = !!user && !isSuperadmin && !!user.access_state && user.access_state !== "active";
  const isReadonly = !!user?.impersonation || user?.role === "tenant_readonly" || subscriptionDisabled;
  const canWrite = !!user && !isReadonly && !isSuperadmin;

  return (
    <AuthCtx.Provider value={{ user, login, logout, refreshUser, loading, isSuperadmin,
      startImpersonation, stopImpersonation,
      isReadonly, subscriptionDisabled, canWrite, contractStatus, contractLoading,
      contractError, refreshContractStatus, completeContractAcceptance }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
